<template>
  <div>
    <portal to="entry_header">
      <h1>Upload video for this entry</h1>
    </portal>
    <p>
      Your video will be securely stored on the <a
        target="_blank"
        href="https://www.soobahkdoinstitute.com/"
      >Soo Bahk Do Institute website</a> and once it has been processed you will be able to preview it from this page.
    </p>
    <div
      v-if="!entry.isSaved()"
      class="uk-alert"
    >
      <span class="uk-icon-info-circle" /> You can return to this step later if you do not have a video prepared yet.
    </div>
    <div v-if="fileReferences.length === 0">
      <div

        class="block block--uploadTarget block--compact"
        @click="selectFiles"
      >
        <span class="uk-icon-paperclip" /> {{ $t('events.entry_attach_video_file') }}
      </div>

      <div
        class="uk-text-right"
        style="margin: 3px 0;"
      >
        <a
          v-if="!disabled"
          class="uk-text-danger"
          href="#"
          @click.prevent="clickTroubleUploading"
        >trouble uploading?</a>
      </div>
    </div>

    <div
      v-if="isOffline"
      class="uk-alert uk-alert-danger"
    >
      <p>You are currently offline, your device does not appear to be connected to the internet and your upload will fail. Please check your network settings and try again.</p>
    </div>

    <Attachment
      v-for="fRef in fileReferences"
      :key="fRef"
      :reference="fRef"
      @deleted="removeRef"
      @retrying="retryingUpload"
    />

    <a
      v-if="entry.hasCompletedVideo()"
      href="#"
      class="uk-button uk-button-primary uk-width-1-1 uk-margin"
      @click.prevent="$emit('preview-video')"
    ><span class="uk-icon-film" /> Preview or edit your video</a>

    <div
      v-if="uploadIsFlaky || backupUrl.length > 0"
      class="uk-form uk-margin"
    >
      <input-row
        v-model="backupUrl"
        label="Link to video file:"
        type="url"
        placeholder="https://www.example.com/your-video-file.mp4"
      />

      <p class="uk-alert">
        If you're having trouble uploading, please enter a link to the video file. You can store the file for free on <a
          href="https://www.dropbox.com/"
          target="_blank"
        >Dropbox <span class="uk-icon-external-link" /></a>, <a
          href="https://www.google.com/intl/en_gh/drive/"
          target="_blank"
        >Google Drive <span class="uk-icon-external-link" /></a>, <a
          href="https://www.microsoft.com/en-us/microsoft-365/onedrive/online-cloud-storage"
          target="_blank"
        >One Drive <span class="uk-icon-external-link" /></a> or other similar cloud storage services.
      </p>
    </div>

    <portal
      to="entry_before_footer"
    >
      <div v-if="(triedValidation && !hasVideoFile) || errors.attachment_references">
        <div class="uk-alert uk-alert-danger">
          {{ errors.attachment_references || $t('events.you_must_upload_a_video') }}
        </div>
      </div>
    </portal>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Watch} from 'vue-property-decorator';
import FormStep from "./FormStep";
import Attachment from '../uploader/Attachment.vue';
import {validationMixin} from "vuelidate";
import EventEntryModel from "../../models/EventEntryModel";
import UploadedFileModel from "../../models/UploadedFile";
import inputRow from "../form/input-row.vue";

@Component({
  components: {
    Attachment,
    'input-row': inputRow,
  },
  mixins: [validationMixin],
  validations: {
  }
})
export default class UploadStep extends FormStep {
  @Prop({default: ''}) readonly entryRef!: string;
  @Prop() readonly entry!: EventEntryModel;
  @Prop({default: false}) readonly disabled!: boolean;

  triedValidation = false;
  backupUrl = '';
  isOffline = false;
  hasBeenOffline = false;
  isFlaky = false;

  created(): void {
    window.addEventListener('offline', () => {
      this.isOffline = true;
      this.hasBeenOffline = true;
    });
    window.addEventListener('online', () => {
      this.isOffline = false;
    });
  }

  data(): any {
    return {
      backupUrl: this.entry.data.backup_video_url,
    }
  }

  selectFiles(): void {
    if (!this.disabled) {
      this.$emit('select-files');
    }
  }

  clickTroubleUploading(): void {
    this.isFlaky = !this.isFlaky;
  }

  get uploadIsFlaky(): boolean {
    return this.hasBeenOffline || this.isFlaky;
  }

  get hasVideoFile(): boolean {
    if (this.backupUrl.length > 0) {
      return true;
    }

    if (!this.entry.isSaved() || !this.entry.isMissingVideo()) {
      return true;
    }

    const files: UploadedFileModel[] = this.$store.getters['files/filesByRefs'](this.fileReferences);

    for (const file of files) {
      if (file.data.file_type === 'video'&& file.data.is_complete) {
        return true;
      }
    }

    return false;
  }

  get fileReferences(): string[] {
    return Array.from(new Set([...this.entry.data.attachment_references, ...this.$store.getters['events/files/allFromEntry'](this.entryRef)]));
  }

  runValidation(silent = false): void {
    this.triedValidation = !silent;
    this.$emit('valid', true);
  }

  removeRef(ref: string): void {
    this.isFlaky = true;
    this.$store.dispatch('events/files/remove', {entry_ref: this.entryRef, file_ref: ref});

    this.$emit('remove-attachment-reference', ref);
  }

  retryingUpload(): void {
    this.isFlaky = true;
  }

  setBackupFile(v: string): void {
    this.$emit('set-backup-file', v);
  }

  @Watch('hasVideoFile', {immediate: true})
  validate(nV: boolean, oV: boolean): void {
    if (oV === true && nV === false) {
      this.triedValidation = true;
    }
    this.$emit('valid', nV);
  }

  @Watch('backupUrl')
  setBackupUrl(v: string): void {
    this.$emit('set-backup-file', v);
  }
}
</script>

<style scoped>

</style>
